import { StaticInfoType } from '@libs/trip-planner-interfaces';

import type { GetHotelsEndpointIncludedProperties } from './types';

const OVERVIEW_PROPERTIES: string[] = [
  'Hotels.HotelCode',
  'Hotels.Name',
  ['Hotels.Address.City', 'Hotel.Address.Country'],
  ['Hotels.Images.ImageName', 'Hotels.Images.ImageService'],
  'Hotels.SemboSunRating',
  [
    'Hotels.Position.Latitude.DecimalDegrees',
    'Hotels.Position.Longitude.DecimalDegrees',
  ],
  'Hotels.TypeOfHotel',
  ['Hotels.MainPolygon.Id', 'Hotels.MainPolygon.FullName'],
].flat();

const OVERVIEW_ACTIVITY_PROPERTIES: string[] = [
  'Hotels.HotelCode',
  'Hotels.Name',
  ['Hotels.Images.ImageName', 'Hotels.Images.ImageService'],
  'Hotels.SemboSunRating',
  'Hotels.TypeOfHotel',
  'Hotels.MainPolygon.FullName',
].flat();

const POLYGON_PROPERTIES: string[] = [
  'Polygons.PolygonId',
  'Polygons.Type',
  'Polygons.FullName',
];

const OVERVIEW_HEARTED_HOTEL_PROPERTIES: string[] = [
  'Hotels.HotelCode',
  'Hotels.Name',
  ['Hotels.Address.City', 'Hotels.Address.Country'],
  ['Hotels.Images.ImageName', 'Hotels.Images.ImageService'],
  'Hotels.SemboSunRating',
  [
    'Hotels.Position.Latitude.DecimalDegrees',
    'Hotels.Position.Longitude.DecimalDegrees',
  ],
  'Hotels.TypeOfHotel',
  ['Hotels.MainPolygon.Id', 'Hotels.MainPolygon.FullName'],
  'Hotels.PolygonIds',
  ...POLYGON_PROPERTIES,
].flat();

const DESTINATION_PROPERTIES: string[] = [
  'Hotels.HotelCode',
  'Hotels.Name',
  ['Hotels.Images.ImageName', 'Hotels.Images.ImageService'],
  'Hotels.SemboSunRating',
  [
    'Hotels.Position.Latitude.DecimalDegrees',
    'Hotels.Position.Longitude.DecimalDegrees',
  ],
  'Hotels.PopularityScore',
  'Hotels.PolygonIds',
  'Hotels.TypeOfHotel',
  ...POLYGON_PROPERTIES,
  [
    'Hotels.OpeningParagraph.TwoLetterIsoLanguageCode',
    'Hotels.OpeningParagraph.Value',
  ],
  ['Hotels.Address.City', 'Hotels.Address.Country'],
  ['Hotels.MainPolygon.Id', 'Hotels.MainPolygon.FullName'],
].flat();

const TAG_INFORMATION_PROPERTIES: string[] = [
  'TagInformation.Tag',
  'TagInformation.Icon.ImageUrl',
];

const DETAILS_PROPERTIES = [
  'Hotels.HotelCode',
  'Hotels.Name',
  ['Hotels.Description.TwoLetterIsoLanguageCode', 'Hotels.Description.Value'],
  [
    'Hotels.OpeningParagraph.TwoLetterIsoLanguageCode',
    'Hotels.OpeningParagraph.Value',
  ],
  ['Hotels.Address.City', 'Hotels.Address.Country'],
  ['Hotels.Images.ImageName', 'Hotels.Images.ImageService'],
  'Hotels.Properties',
  'Hotels.SemboSunRating',
  [
    'Hotels.Position.Latitude.DecimalDegrees',
    'Hotels.Position.Longitude.DecimalDegrees',
  ],
  'Hotels.Distances',
  'Hotels.PolygonIds',
  'Hotels.TypeOfHotel',
  'Hotels.Tags.Name',
  ['Hotels.MainPolygon.Id', 'Hotels.MainPolygon.FullName'],
  [
    'Hotels.SeatMap',
    'Hotels.SeatMap.Images',
    'Hotels.SeatMap.Images.Name',
    'Hotels.SeatMap.Images.Service',
  ],
  POLYGON_PROPERTIES,
  TAG_INFORMATION_PROPERTIES,
].flat();

const ACTIVITY_SUGGESTION_PROPERTIES = [
  'Hotels.HotelCode',
  'Hotels.Name',
  ['Hotels.Images.ImageName', 'Hotels.Images.ImageService'],
].flat();

export const GET_HOTELS_ENDPOINT_PROPERTIES: Record<
  StaticInfoType,
  GetHotelsEndpointIncludedProperties
> = {
  [StaticInfoType.Overview]: {
    includeValues: OVERVIEW_PROPERTIES,
    includeGroupValues: ['Hotels.Amenities', 'Hotels.GuestRatings'],
  },
  [StaticInfoType.OverviewActivity]: {
    includeValues: OVERVIEW_ACTIVITY_PROPERTIES,
    includeGroupValues: [],
  },
  [StaticInfoType.OverviewHeartedHotel]: {
    includeValues: OVERVIEW_HEARTED_HOTEL_PROPERTIES,
    includeGroupValues: [],
  },
  [StaticInfoType.Destination]: {
    includeValues: DESTINATION_PROPERTIES,
    includeGroupValues: ['Hotels.Amenities', 'Hotels.GuestRatings'],
  },
  [StaticInfoType.Details]: {
    includeValues: DETAILS_PROPERTIES,
    includeGroupValues: ['Hotels.Amenities', 'Hotels.GuestRatings'],
  },
  [StaticInfoType.NameOnly]: {
    includeValues: ['Hotels.HotelCode', 'Hotels.Name'],
    includeGroupValues: [],
  },
  [StaticInfoType.MainPolygonOnly]: {
    includeValues: ['Hotels.MainPolygon.FullName', 'Hotels.MainPolygon.Id'],
    includeGroupValues: [],
  },
  [StaticInfoType.PositionOnly]: {
    includeValues: [
      'Hotels.Position.Latitude.DecimalDegrees',
      'Hotels.Position.Longitude.DecimalDegrees',
    ],
    includeGroupValues: [],
  },
  [StaticInfoType.NeighborhoodPolygonsOnly]: {
    includeValues: [
      'Hotels.NeighborhoodPolygons',
      'Hotels.NeighborhoodPolygons.Id',
      'Hotels.NeighborhoodPolygons.FullName',
      'Hotels.NeighborhoodPolygons.Name',
      'Hotels.NeighborhoodPolygons.Score',
    ],
    includeGroupValues: [],
  },
  [StaticInfoType.ActivitySuggestions]: {
    includeValues: ACTIVITY_SUGGESTION_PROPERTIES,
    includeGroupValues: [],
  },
};
